export type DiscoveryCardPosition = 'top' | 'bottom' | 'left' | 'right' | 'center' | 'top-right' | 'top-left';

interface Size {
    width: number;
    height: number;
}

interface Coordinates {
    x: number;
    y: number;
}

interface RectBox {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
}

export interface CardPositionResult {
    left: number | string;
    top: number | string;
    x: number | string;
    y: number | string;
}

function clamp(value: number, min: number, max: number): number {
    if (max < min) return min;
    return Math.min(max, Math.max(min, value));
}

function isSameCoordinates(a: Coordinates, b: Coordinates): boolean {
    return a.x === b.x && a.y === b.y;
}

function overflowScore(candidate: Coordinates, cardSize: Size, viewport: Size, margin: number): number {
    let score = 0;
    const minX = margin;
    const minY = margin;
    const maxX = viewport.width - margin;
    const maxY = viewport.height - margin;

    if (candidate.x < minX) score += minX - candidate.x;
    if (candidate.y < minY) score += minY - candidate.y;
    if (candidate.x + cardSize.width > maxX) score += (candidate.x + cardSize.width) - maxX;
    if (candidate.y + cardSize.height > maxY) score += (candidate.y + cardSize.height) - maxY;

    return score;
}

function normalizeRectForViewport(rect: DOMRect, viewport: Size, margin: number): RectBox {
    const minX = margin;
    const minY = margin;
    const maxX = viewport.width - margin;
    const maxY = viewport.height - margin;

    const left = clamp(rect.left, minX, maxX);
    const top = clamp(rect.top, minY, maxY);
    const right = clamp(rect.right, minX, maxX);
    const bottom = clamp(rect.bottom, minY, maxY);

    const normalizedLeft = Math.min(left, right);
    const normalizedTop = Math.min(top, bottom);
    const normalizedRight = Math.max(left, right);
    const normalizedBottom = Math.max(top, bottom);

    return {
        left: normalizedLeft,
        top: normalizedTop,
        right: normalizedRight,
        bottom: normalizedBottom,
        width: Math.max(1, normalizedRight - normalizedLeft),
        height: Math.max(1, normalizedBottom - normalizedTop),
    };
}

function buildCandidates(
    rect: RectBox,
    preferredPosition: DiscoveryCardPosition,
    cardSize: Size,
    gap: number
): Coordinates[] {
    const centeredX = rect.left + (rect.width / 2) - (cardSize.width / 2);
    const centeredY = rect.top + (rect.height / 2) - (cardSize.height / 2);

    const options: Record<string, Coordinates> = {
        top: { x: centeredX, y: rect.top - cardSize.height - gap },
        bottom: { x: centeredX, y: rect.bottom + gap },
        left: { x: rect.left - cardSize.width - gap, y: centeredY },
        right: { x: rect.right + gap, y: centeredY },
        topRight: { x: rect.right - cardSize.width, y: rect.top - cardSize.height - gap },
        // Requested behavior for right/bottom edge targets:
        // keep popup inside the screen with popup bottom-right near target top-left.
        aboveLeft: { x: rect.left - cardSize.width - gap, y: rect.top - cardSize.height - gap },
    };

    const byPreference: Record<DiscoveryCardPosition, Coordinates[]> = {
        top: [options.top, options.bottom, options.right, options.left, options.aboveLeft],
        bottom: [options.bottom, options.top, options.aboveLeft, options.left, options.right],
        left: [options.left, options.right, options.top, options.bottom, options.aboveLeft],
        right: [options.right, options.left, options.top, options.bottom, options.aboveLeft],
        'top-right': [options.topRight, options.top, options.aboveLeft, options.left, options.right, options.bottom],
        'top-left': [options.aboveLeft, options.top, options.left, options.bottom, options.right],
        center: [{ x: centeredX, y: centeredY }],
    };

    return byPreference[preferredPosition] ?? byPreference.center;
}

export function calculateCardPosition(
    rect: DOMRect | null,
    preferredPosition: DiscoveryCardPosition,
    cardSize: Size,
    viewportOverride?: Size
): CardPositionResult {
    const margin = 20;
    const gap = 20;
    const viewport = viewportOverride ?? {
        width: typeof window !== 'undefined' ? window.innerWidth : cardSize.width + (margin * 2),
        height: typeof window !== 'undefined' ? window.innerHeight : cardSize.height + (margin * 2),
    };

    if (!rect || preferredPosition === 'center') {
        return {
            left: '50%',
            top: '50%',
            x: '-50%',
            y: '-50%',
        };
    }

    const normalizedCardSize: Size = {
        width: Math.max(1, Math.min(cardSize.width, viewport.width - (margin * 2))),
        height: Math.max(1, Math.min(cardSize.height, viewport.height - (margin * 2))),
    };

    const normalizedRect = normalizeRectForViewport(rect, viewport, margin);

    const nearRightEdge = normalizedRect.right > (viewport.width - Math.max(180, normalizedCardSize.width * 0.5));
    const nearBottomEdge = normalizedRect.bottom > (viewport.height - Math.max(180, normalizedCardSize.height * 0.5));
    const preferredCandidates = buildCandidates(normalizedRect, preferredPosition, normalizedCardSize, gap);

    // Strongly prefer above-left anchor for edge targets to keep the card visible.
    const edgePriority = {
        x: normalizedRect.left - normalizedCardSize.width - gap,
        y: normalizedRect.top - normalizedCardSize.height - gap,
    };
    const candidates = (nearRightEdge || nearBottomEdge)
        ? [edgePriority, ...preferredCandidates.filter(candidate => !isSameCoordinates(candidate, edgePriority))]
        : preferredCandidates;

    let best = candidates[0];
    let bestScore = overflowScore(best, normalizedCardSize, viewport, margin);

    for (const candidate of candidates) {
        const score = overflowScore(candidate, normalizedCardSize, viewport, margin);
        if (score === 0) {
            best = candidate;
            bestScore = score;
            break;
        }
        if (score < bestScore) {
            best = candidate;
            bestScore = score;
        }
    }

    const x = clamp(best.x, margin, viewport.width - normalizedCardSize.width - margin);
    const y = clamp(best.y, margin, viewport.height - normalizedCardSize.height - margin);

    return { x, y, left: 0, top: 0 };
}
